import VRCode from '@/api/vrCode/api.js'
import {
  Auth,
  setLogged,
  setToken,
} from '../../../utils/auth'

const vrCode = new VRCode()

const vrCodeModule = {
  namespaced: true,
  state: {
    currentCode: null,
    codeStatus: {
      expired: false,
      used: false,
      timeLeft: 0,
      expiresAt: null,
    },
    load: false,
    loadStatus: false,
    error: null,
    countdownInterval: null,
  },

  getters: {
    currentCode(state) {
      return state.currentCode
    },

    codeStatus(state) {
      return state.codeStatus
    },

    load(state) {
      return state.load
    },

    loadStatus(state) {
      return state.loadStatus
    },

    error(state) {
      return state.error
    },

    hasActiveCode(state) {
      return state.currentCode && !state.codeStatus.expired && !state.codeStatus.used
    },

    isCodeExpired(state) {
      return state.codeStatus.expired
    },

    isCodeUsed(state) {
      return state.codeStatus.used
    },

    timeLeftInMinutes(state) {
      return state.codeStatus.timeLeft
    },
  },

  mutations: {
    SET_LOAD(state, load) {
      state.load = load
    },

    SET_LOAD_STATUS(state, loadStatus) {
      state.loadStatus = loadStatus
    },

    SET_ERROR(state, error) {
      state.error = error
    },

    SET_CODE(state, code) {
      state.currentCode = code
    },

    SET_CODE_STATUS(state, status) {
      state.codeStatus = { ...state.codeStatus, ...status }
    },

    CLEAR_CODE(state) {
      state.currentCode = null
      state.codeStatus = {
        expired: false,
        used: false,
        timeLeft: 0,
        expiresAt: null,
      }
      state.error = null
    },

    DECREMENT_TIME_LEFT(state) {
      if (state.codeStatus.timeLeft > 0) {
        state.codeStatus.timeLeft--
      }

      if (state.codeStatus.timeLeft === 0) {
        state.codeStatus.expired = true
      }
    },

    SET_COUNTDOWN_INTERVAL(state, interval) {
      state.countdownInterval = interval
    },

    CLEAR_COUNTDOWN_INTERVAL(state) {
      if (state.countdownInterval) {
        clearInterval(state.countdownInterval)
        state.countdownInterval = null
      }
    },
  },

  actions: {
    generate({ commit, dispatch }) {
      return new Promise((resolve, reject) => {
        commit('SET_LOAD', true)
        commit('SET_ERROR', null)
        commit('CLEAR_CODE')

        vrCode.generate()
          .then(response => {
            console.log('Generate API Response:', response)

            if (response && response.data && response.data.success) {
              const { data } = response.data

              if (data && data.code && data.time_remaining > 0) {
                commit('SET_CODE', data.code)
                commit('SET_CODE_STATUS', {
                  expired: false,
                  used: false,
                  timeLeft: Math.ceil(data.time_remaining / 60),
                  expiresAt: data.expires_at,
                })

                dispatch('startCountdown')

                console.log('Code set successfully:', {
                  code: data.code,
                  timeLeft: Math.ceil(data.time_remaining / 60),
                })
              } else {
                throw new Error('Invalid response data structure')
              }
            } else if (response && response.success) {
              const { data } = response

              if (data && data.code && data.time_remaining > 0) {
                commit('SET_CODE', data.code)
                commit('SET_CODE_STATUS', {
                  expired: false,
                  used: false,
                  timeLeft: Math.ceil(data.time_remaining / 60),
                  expiresAt: data.expires_at,
                })

                dispatch('startCountdown')
              } else {
                throw new Error('Invalid response data')
              }
            } else {
              throw new Error('Invalid response format')
            }

            commit('SET_LOAD', false)
            resolve(response)
          })
          .catch(error => {
            console.error('Generate VR Code Error:', error)
            const errorMessage = error.response?.data?.message
                            || error.message
                            || 'Failed to generate VR code'
            commit('SET_ERROR', errorMessage)
            commit('SET_LOAD', false)
            commit('CLEAR_CODE')
            reject(error)
          })
      })
    },

    checkStatus({ commit }) {
      return new Promise((resolve, reject) => {
        commit('SET_LOAD_STATUS', true)
        commit('SET_ERROR', null)

        vrCode.checkStatus()
          .then(response => {
            console.log('Check Status API Response:', response)

            if (response && response.data && response.data.success) {
              const { data } = response.data

              if (data && data.code && data.time_remaining > 0) {
                commit('SET_CODE', data.code)
                commit('SET_CODE_STATUS', {
                  expired: data.is_expired || data.time_remaining <= 0,
                  used: data.is_used || false,
                  timeLeft: Math.ceil(data.time_remaining / 60) || 0,
                  expiresAt: data.expires_at,
                })
              } else {
                console.log('Code data invalid or expired, clearing...')
                commit('CLEAR_CODE')
              }
            } else {
              console.log('No active code found, clearing state...')
              commit('CLEAR_CODE')
            }

            commit('SET_LOAD_STATUS', false)
            resolve(response)
          })
          .catch(error => {
            console.error('Check Status Error:', error)

            if (error.response && error.response.status === 404) {
              console.log('No active code (404), clearing state...')
              commit('CLEAR_CODE')
              commit('SET_LOAD_STATUS', false)
              resolve({ success: false, message: 'No active code' })
              return
            }

            const errorMessage = error.response?.data?.message || 'Failed to check code status'
            commit('SET_ERROR', errorMessage)
            commit('SET_LOAD_STATUS', false)
            reject(error)
          })
      })
    },

    cancel({ commit }) {
      return new Promise((resolve, reject) => {
        commit('SET_LOAD', true)
        commit('SET_ERROR', null)

        vrCode.cancel()
          .then(response => {
            if (response && (response.success || (response.data && response.data.success))) {
              commit('CLEAR_CODE')
              commit('CLEAR_COUNTDOWN_INTERVAL')
            }
            commit('SET_LOAD', false)
            resolve(response)
          })
          .catch(error => {
            const errorMessage = error.response?.data?.message || 'Failed to cancel VR code'
            commit('SET_ERROR', errorMessage)
            commit('SET_LOAD', false)
            reject(error)
          })
      })
    },

    login({ commit }, code) {
      return new Promise((resolve, reject) => {
        commit('SET_LOAD', true)
        commit('SET_ERROR', null)

        vrCode.login(code)
          .then(response => {
            console.log('loginnnnn', response)
            if (response && (response.success || (response.data && response.data.success))) {
              commit('CLEAR_CODE')
              commit('CLEAR_COUNTDOWN_INTERVAL')
              const token = response.access_token || response.data?.access_token
              console.log('ssssssssss', token)
              if (token) {
                setLogged('1')
                setToken(token)
                Auth(response.data || response)
              }
            }
            commit('SET_LOAD', false)
            resolve(response)
          })
          .catch(error => {
            const errorMessage = error.response?.data?.message || 'Failed to login with VR code'
            commit('SET_ERROR', errorMessage)
            commit('SET_LOAD', false)
            reject(error)
          })
      })
    },

    startCountdown({ commit, getters, dispatch }) {
      return new Promise(resolve => {
        commit('CLEAR_COUNTDOWN_INTERVAL')

        if (getters.hasActiveCode && getters.timeLeftInMinutes > 0) {
          console.log('Starting countdown with', getters.timeLeftInMinutes, 'minutes left')

          const interval = setInterval(() => {
            if (getters.hasActiveCode && getters.timeLeftInMinutes > 0) {
              commit('DECREMENT_TIME_LEFT')
              console.log('Time left:', getters.timeLeftInMinutes, 'minutes')
            } else {
              console.log('Countdown finished, clearing interval')
              commit('CLEAR_COUNTDOWN_INTERVAL')
              dispatch('checkStatus')
            }
          }, 60000)

          commit('SET_COUNTDOWN_INTERVAL', interval)
        }
        resolve()
      })
    },

    stopCountdown({ commit }) {
      return new Promise(resolve => {
        commit('CLEAR_COUNTDOWN_INTERVAL')
        resolve()
      })
    },

    clearError({ commit }) {
      return new Promise(resolve => {
        commit('SET_ERROR', null)
        resolve()
      })
    },

    reset({ commit }) {
      return new Promise(resolve => {
        commit('CLEAR_CODE')
        commit('CLEAR_COUNTDOWN_INTERVAL')
        commit('SET_ERROR', null)
        resolve()
      })
    },
  },
}

export default vrCodeModule
